import { action, makeObservable, observable } from "mobx"

import { APICode } from "@/api/APICode"
import { IContextualSaveBar } from "@/models/ICommonStore"
import Api from "@/pages/DeliveryGuarantee/Api"
import { AutoInstallStatusEnum, CartSettingApi, CheckWidgetApi, FitEnum, InfoApi, InstallMethodEnum, OverviewApi, SysConfigA } from "@/pages/DeliveryGuarantee/Api/type"
import { auditRetEnum } from "@/pages/DeliveryGuarantee/common"
import { PlanIDEnum } from "@/pages/DeliveryGuarantee/Transactions/planIDenum"
import { decryptUserId } from "@/utils/aesDecrypt"


export default class deliveryGuarantee {
  // 按钮loading
  @observable isApplyBtLoading = false

  // 请求审核的弹窗
  @observable guaranteeModule = false

  // 是否勾选加入免订阅活动
  @observable isCheckJoinActivity = false

  // 加入减免活动弹窗
  @observable isJoinActivityModal = false

  // 高亮弹窗配置
  @observable highlightShow = false

  // 自动适配检测中（避免 insurancePopup 刚变为 true 时 banner 闪烁）
  @observable isAutoFitting = false

  // 欠款未缴账单
  @observable public unpaidBillNum = 0

  // 走完绑卡跟授权切换配置
  @observable public countdownTimer = true

  @observable sysConfig: SysConfigA = {
    plusBlock: 0,
    remindOne: 0,
    remindTwo: 0,
    remindThr: 0,
    remindFour: 0,
    remindFive: 0,
    billTip: true,
    showCommissionBanner: false,
    showInCartBanner: false,
    showClaimBanner: false,
    creditIssuanceHistoryTip: false,
  }

  @observable overviewData: OverviewApi = {
    orders: "",
    revenue: 0,
    sales: 0,
    symbol: "",
    isOrder: false,
    revenueTotal: 0,
    commission: 0,
    claimTotal: 0,
  }

  @observable dateTime = {
    selectTime: "",
    startTime: "",
    endTime: "",
  }

  @observable infoData: InfoApi = {
    user_id: "",
    payNumber: "",
    audit: 0,
    auditRet: "",
    currency: "CNY",
    customEmail: "",
    symbol: "",
    isPaid: false,
    isViewAudit: 0,
    paymentMethod: 0,
    storeName: "",
    isFirstApply: 0,
    fitType: InstallMethodEnum.undefined,
    isFit: FitEnum.NotFitted,
    planDisplayName: "",
    isPlus: false,
    isAuth: true,
    authUrl: "",
    isShowModal: false,
    isShowBanner: false,
    isDisplayStatus: false,
    completedLevel: 0,
    planId: "104001",
    cycle: 1,
    isShowTalk: false,
    subscriptionRebateOptIn: 0,
  }

  @observable cartSetting: CartSettingApi = {
    defaultDisplayStatus: 2,
    fulfillmentType: 2,
    isEnable: false,
    isFit: 2,
    autoPublish: 1,
    isShowTip: false,
    fitType: 0,
    plusBlock: false,
    isBreathe: true,
    isShowShippingProtection: false,
    firstEnable: "",
    autoInstallStatus: AutoInstallStatusEnum.hide,
    widgetType: 0,
    styleConfig: undefined,
  }

  @observable public widgetSaveBar: IContextualSaveBar = {
    disabled: false,
    loading: false,
    show: false,
    unlocking: false,
  }

  @observable public detectionCheck: CheckWidgetApi = {
    appEmbedLink: "",
    // 是否开启
    enableWidget: false,
    scopeUrl: false,
    webExtension: {
      insuranceCheckout: false,
      insurancePopup: true,
    },
  }


  // @observable trackingLinHistoryData:TrackingLinkData | undefined;


  constructor() {
    makeObservable(this)
  }

  // 是否有PPI资格
  get hasPPIQualification() {
    return !!this.infoData.isPaid
  }

  get isOldUser() {
    return this.infoData.planId === "104001"
  }

  get isCommissionsUser() {
    return !this.isOldUser
  }

  // 获取当前档位对应的佣金比例
  get currentPlanCommission() {
    return (PlanIDEnum(this.infoData.planId)?.evShareToMerchant || 0) + "%"
  }

  // 获取当前未完成任务
  get isNotCompletedTask() {
    return !this.infoData.isAuth || this.infoData.paymentMethod === 0 || !this.countdownTimer
  }

  // 判断是否可以直接开启小部件
  get canEnableWidget() {
    return this.infoData.isAuth && this.infoData.paymentMethod > 0 && this.countdownTimer
  }

  // 是否已参加减免活动
  get isJoinedActivity() {
    return this.infoData.subscriptionRebateOptIn === 2
  }

  // 参加减免活动状态 0: 未参加 1: 参加中 2:已参加
  get joinActivityStatus() {
    return this.infoData.subscriptionRebateOptIn
  }

  // 获取当前优先需要完成的任务
  @action.bound
  async getCurrentPriorityTask() {
    const isAddedPlusBlock = this.infoData.isPlus ? await this.isAddedPlusBlock() : this.detectionCheck.webExtension.insurancePopup

    if (this.infoData.paymentMethod === 0 ) {
      return "payment"
    } else if (!this.infoData.isAuth) {
      return "auth"
    } else if (!isAddedPlusBlock) {
      return "block"
    } else if (!this.cartSetting.isEnable){
      return "enable"
    }
    return ""
  }

  // 判断是否添加plus block
  @action.bound
  async isAddedPlusBlock(cb?: (isAdded: boolean) => void) {
    if (!this.infoData.isPlus) {
      return false
    }

    let isAdded = false

    if (window.shopify && shopify?.app && this.infoData.isPlus) {
      const widgetList = await shopify.app.extensions()
      const widget = widgetList.find(item => item.handle === "ins-checkout")

      isAdded = !!widget?.activations?.length

      // 判断后端存放的状态有变更则更新
      if (isAdded !== Boolean(this.cartSetting.plusBlock)) {
        this.setCartSetting({
          ...this.cartSetting,
          plusBlock: isAdded,
        })
        await Api.setBlock({ plusBlock: isAdded ? 1 : 0 })
        
        cb?.(isAdded)
      }

      return isAdded
    }

    return Boolean(this.cartSetting.plusBlock)
  }

  @action.bound
  setHighlightShow(v: boolean) {
    this.highlightShow = v
  }

  @action.bound
  setGuaranteeModule(v: boolean) {
    this.guaranteeModule = v
  }

  @action.bound
  setJoinActivityModal(v: boolean) {
    this.isJoinActivityModal = v
  }


  @action.bound
  async detectionCheckWidget(skipCheck = false, cb?: (isEnabled: boolean) => void) {
    const isQuery = (this.infoData.isFirstApply === 1 && this.infoData.isAuth) || skipCheck

    if (isQuery) {
      const res = await Api.detectionCheckWidget()

      if (res.data.code === APICode.OK) {
        this.detectionCheck = res.data?.data
        cb?.(res.data?.data.webExtension?.insurancePopup)
      }

    }
  }

  @action.bound
  async applicationAuditAndShowModalOrNot(isJoinActivity?: boolean): Promise<boolean> {

    const resAudit = await Api.auditReview({ joinSubscriptionRebate: isJoinActivity ? 1 : undefined })

    if (resAudit.data.code === APICode.OK) {
      await this.getInfo()
      return resAudit.data.data.auditRet === auditRetEnum.success
    }
    return false
  }

  @action.bound
  setCartSetting(value:CartSettingApi) {
    // value.plusBlock = false
    this.cartSetting = value
  }

  // 前端扩展检测结果与后端 added 不一致时，同步更新本地与后端
  @action.bound
  async syncCustomerAccountAdded(added: boolean) {
    const ca = this.cartSetting.customerAccount

    if (!ca || ca.added === added) return
    this.setCartSetting({
      ...this.cartSetting,
      customerAccount: { ...ca, added },
    })
    await Api.setCustomerAccountAdded({ added })
  }

  @action.bound
  setWidgetSaveBar(key: keyof IContextualSaveBar, btnText: any): void {
    this.widgetSaveBar[key] = btnText as never
  }

  @action.bound
  setSysConfig(value:SysConfigA): void {
    this.sysConfig = value
  }

  @action.bound
  async setSysConfigByKey(key:keyof SysConfigA, val:any, syncToServer = true){
    if (syncToServer){
      const res =  await Api.setSysConfig({
        [key]: val,
      })

      if (res.data.code !== 200) return
    }
    this.setSysConfig({
      ...this.sysConfig,
      [key]: val,
    })
  }


  @action.bound
  async getSysConfig() {
    const o = await Api.getSysConfig()

    if (o.data.code === 200) {
      this.setSysConfig(o.data.data)
    }
  }

  @action.bound
  async  getCartSetting() {
    const o =  await  Api.getCartSetting()

    if (o.data.code === 200) {
      const { data } = o.data

      // data.isEnable = true
      // data.plusBlock = false
      // data.isShowShippingProtection = false
      
      this.setCartSetting(data)
      return data
    }
    return null
  }

  @action.bound
  // 获取用户数据
  async getInfo() {
    const res = await Api.getInfo()

    if (res.data.code === APICode.OK) {
 
      const { data } = res.data

      // data.isFirstApply = 1
      // data.isPaid = true
      // data.isPlus = true
      const userId = /^\d+$/.test(data.user_id) ? data.user_id : await decryptUserId(data.user_id)

      console.log("userId", userId)

      this.infoData = {
        ...data,
        user_id: userId,
      }
    }

  }

  @action.bound
  // 获取用户数据
  async getOverviewData(dataTime: string, startTime?:string, endTime?:string) {

    this.dateTime.selectTime = dataTime
    // @ts-ignore
    this.dateTime.startTime = startTime
    // @ts-ignore
    this.dateTime.endTime = endTime

    const data = dataTime === "Custom" ? {
      dateFrom: startTime,
      dateTo: endTime,
    } : {
      claimAt: dataTime.toUpperCase()
        .replace(/\s+/g, "_"),
    }

    const res = await Api.getOverviewData(data)

    if (res.data.code === APICode.OK) {
      this.overviewData = res.data.data
    }

  }

  @action.bound
  getUnpaidBillNum(){
    Api.getTransactUnpaidCount().then(res => {
      if (res.data.code === APICode.OK) {
        this.unpaidBillNum = res.data.data.total ? Number(res.data.data.total) : 0
      }
    })
  }

}
